import React, { useState } from 'react';
import styled from 'styled-components';
import { navigate } from '@gatsbyjs/reach-router';
import Tabs from '~/components/molecule/Tabs';
import TabBody from '~/components/molecule/Tabs/components/TabBody';
import { Heading3, Body } from '~/components/atom/Typography';
import TEST_ID from './index.testid';

import {
  ConnectorOperator,
  SortDirection,
  SortFieldAdvanced,
  useGetContactsQuery,
} from '~/graphql/types';
import Button from '~/components/atom/Button';
import SimpleTable, { Columns, Row } from '~/components/organism/SimpleTable';
import Link from '~/components/molecule/Link';
import truncateName from '../../utils/truncateName';
import Loading from '~/components/organism/SimpleTable/components/Loading';
import CardHeader from '../CardHeader';
import getDateRangeOptions from './utils/getDateRangeOptions';
import Icon from '~/components/atom/Icon';
import LeadScoreDisplayer from '~/components/molecule/LeadScoreDisplayer';
import useCurrentAccount from '~/hooks/useCurrentAccount';
import withMasonryCard from '../withMasonryCard';
import useTrackEvent from '~/hooks/useTrackEvent';

export type Props = {};

const getColumns = (): Columns => ({
  LeadScore: {
    header: <IconHeader name="lead-score" />,
    format: (value: number) => <LeadScoreDisplayer leadScore={value} />,
  },
  name: {
    header: 'Naam',
    format: (value: string, row: Row) => (
      <Link to={`/-/contacts/${row.id}`} style={{ whiteSpace: 'nowrap' }}>
        {truncateName(value)}
      </Link>
    ),
  },
  lastActivity: {
    header: 'Laatste activiteit',
    format: (_, row) => row?.lastActivity?.summary ?? '',
  },
});

const text = {
  heading: 'Openstaande kansen',

  noContactsHeader: 'Geen contacten',
  noContactBody: 'Je hebt nog geen contacten aangemaakt.',
  noContactWithLeadScoreBody:
    'Er zijn geen contacten met sterke betrokkenheid.',
  noContactCTA: 'Maak een contact aan',

  errorHeader: 'Uh-oh 😱',
  errorBody:
    'Het lijkt er op dat er iets mis is gegaan bij het ophalen van jouw contacten. Probeer het later opnieuw.',
};

export const defaultQueryVariables = {
  query: '',
  limit: 5,
  nodes: {
    connector: ConnectorOperator.And,
    nodes: [],
  },
};

const nodesMap: Record<
  SortFieldAdvanced.ThreeDayChange | SortFieldAdvanced.SevenDayChange,
  Array<{
    threeDayChange?: { lte: number };
    sevenDayChange?: { lte: number };
    leadScore?: { eq: number; negate: boolean };
  }>
> = {
  [SortFieldAdvanced.ThreeDayChange]: [
    { threeDayChange: { lte: 90 } },
    {
      leadScore: { eq: 0, negate: true },
    },
  ],
  [SortFieldAdvanced.SevenDayChange]: [
    {
      sevenDayChange: { lte: 90 },
    },
    {
      leadScore: { eq: 0, negate: true },
    },
  ],
};

const isKeyOfNodesMap = (
  field: SortFieldAdvanced,
): field is keyof typeof nodesMap => field in nodesMap;

const tabsConfig = [
  {
    label: 'Sterke betrokkenheid',
    direction: SortDirection.Desc,
  },
  {
    label: 'Dalende betrokkenheid',
    direction: SortDirection.Asc,
  },
];

const allTimeNode = {
  Leaf: {
    Contact: { Details: { leadScore: { eq: 0, negate: true } } },
  },
};

const ChangeInLeadScore: React.FCC<Props> = () => {
  const { id: accountId } = useCurrentAccount();
  const trackEvent = useTrackEvent();
  const [selectedOptionIndex, setSelectedOptionIndex] = useState<number>(0);
  const [sortDirection, setSortingDirection] = useState<SortDirection>(
    SortDirection.Desc,
  );
  const options = getDateRangeOptions();
  const selectedField = options[selectedOptionIndex].payload;

  const variables = {
    accountId,
    query: '',
    limit: 5,
    nodes: {
      connector: ConnectorOperator.And,
      nodes:
        selectedField === SortFieldAdvanced.OneDayChange ||
        !isKeyOfNodesMap(selectedField)
          ? [allTimeNode]
          : nodesMap[selectedField].map(field => ({
              Leaf: { Contact: { Details: field } },
            })),
    },
    sortBy: {
      direction: sortDirection,
      field: selectedField,
    },
  };

  const { data, error, loading } = useGetContactsQuery({
    variables,
  });

  const FilledCardHeader = () => (
    <CardHeader
      headingText={text.heading}
      onOptionChange={({ selectedOptionIdx }) =>
        setSelectedOptionIndex(selectedOptionIdx)
      }
      options={options}
      selectedOptionIndex={selectedOptionIndex}
      dataTestId={TEST_ID.CARD_HEADER}
    />
  );

  if (error) {
    return (
      <>
        <FilledCardHeader />
        <Heading3 size="m">{text.errorHeader}</Heading3>
        <Body>{text.errorBody}</Body>
      </>
    );
  }

  if (data && data.getContacts.totalContactsInAccount <= 0) {
    return (
      <>
        <FilledCardHeader />
        <Heading3 size="m" dataTestId={TEST_ID.NO_CONTACTS}>
          {text.noContactsHeader}
        </Heading3>
        <Body>{text.noContactBody}</Body>
        <Button
          size="medium"
          onClick={() => {
            /** ----- TRACKING: db_newcontact_clicked ----- */
            trackEvent({
              eventName: 'db_newcontact_clicked',
            });
            return navigate('/-/contacts');
          }}
          label={text.noContactCTA}
        />
      </>
    );
  }

  if (data && data.getContacts.items.length <= 0) {
    return (
      <>
        <FilledCardHeader />
        <Heading3 size="m" dataTestId={TEST_ID.NO_CONTACTS_WITH_LEADSCORE}>
          {text.noContactsHeader}
        </Heading3>
        <Body>{text.noContactWithLeadScoreBody}</Body>
      </>
    );
  }

  const rows: Array<Row> = data?.getContacts.items
    ? data?.getContacts.items.map(
        ({ LeadScore, name, lastActivity, id, permission }): Row => ({
          LeadScore,
          name,
          lastActivity,
          id,
          permission,
        }),
      )
    : [];

  return (
    <>
      <FilledCardHeader />
      <Tabs
        onChange={index => setSortingDirection(tabsConfig[index].direction)}
      >
        {tabsConfig.map(({ label }) => (
          <TabBody label={label} key={label}>
            {loading ? (
              <Loading />
            ) : (
              <SimpleTable
                columns={getColumns()}
                rows={rows}
                blurredColumns={['name']}
                dataTestId={TEST_ID.TABLE}
              />
            )}
          </TabBody>
        ))}
      </Tabs>
    </>
  );
};

const IconHeader = styled(Icon)<{}>`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  font-size: 1.2rem;
`;

export default withMasonryCard(ChangeInLeadScore, {
  containerTestId: TEST_ID.CONTAINER,
  elevated: true,
});
